import { useMemo } from "react";
import { useCatalog } from "../context/CatalogContext";
import {
  SHOP_CATEGORIES,
  TRENDING,
  OCCASIONS,
  PERFECT_MATCH,
  HERO_MAIN,
  HERO_PRODUCT,
  HERO_PRODUCT_NAME,
} from "../data/homeContent";

/**
 * Return admin-configured items for a section, or the built-in defaults
 * when the admin hasn't added anything. This keeps the home page never-empty.
 */
export function useHomeSection(section) {
  const { homeItems } = useCatalog();
  return useMemo(() => {
    const custom = (homeItems || [])
      .filter((h) => h.section === section)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    if (custom.length) return custom;
    if (section === "categories")
      return SHOP_CATEGORIES.map((c, i) => ({ ...c, order: i, id: `default-cat-${i}` }));
    if (section === "trending")
      return TRENDING.map((t, i) => ({
        ...t,
        // normalise built-in shape to the same field names
        label: t.title,
        sub: t.tagline,
        image: t.image,
        link: t.to,
        tag: t.tag,
        id: `default-trending-${i}`,
      }));
    if (section === "occasions")
      return OCCASIONS.map((o, i) => ({
        ...o,
        sub: o.caption,
        image: o.image,
        link: o.to,
        id: `default-occ-${i}`,
      }));
    return [];
  }, [homeItems, section]);
}

/**
 * FPM tile options — per parent (metal | occasion | style), returns
 * defaults + admin-added options together, deduped by lowercased label.
 * Admin additions appear after the defaults.
 */
export function useFpmOptions(parent) {
  const { homeItems } = useCatalog();
  return useMemo(() => {
    const custom = (homeItems || [])
      .filter((h) => h.section === "fpm_option" && h.parent === parent)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    let defaults = [];
    if (parent === "metal") {
      defaults = [
        { id: "d-metal-1", label: "Gold",             link: "/collections/gold" },
        { id: "d-metal-2", label: "Silver",           link: "/collections/silver" },
        { id: "d-metal-3", label: "Rose Gold",        link: "/collections/rose-gold" },
        { id: "d-metal-4", label: "Diamond",          link: "/collections/diamond" },
        { id: "d-metal-5", label: "Temple & Antique", link: "/collections/temple-antique" },
      ];
    } else if (parent === "occasion") {
      defaults = [
        { id: "d-occ-1", label: "Wedding",       link: "/collections/temple-antique" },
        { id: "d-occ-2", label: "Festivals",     link: "/collections/gold" },
        { id: "d-occ-3", label: "Gifting",       link: "/style/gifting" },
        { id: "d-occ-4", label: "Birthday",      link: "/style/birthday" },
        { id: "d-occ-5", label: "Anniversary",   link: "/style/anniversary" },
        { id: "d-occ-6", label: "Everyday Wear", link: "/style/everyday" },
      ];
    } else if (parent === "style") {
      defaults = [
        { id: "d-style-1", label: "Antique",   link: "/collections/temple-antique" },
        { id: "d-style-2", label: "Modern",    link: "/collections/rose-gold" },
        { id: "d-style-3", label: "Minimal",   link: "/style/minimal" },
        { id: "d-style-4", label: "Statement", link: "/style/statement" },
      ];
    }
    // Dedupe by lowercased label; admin overrides default when labels collide.
    const seen = new Map();
    for (const d of defaults) seen.set((d.label || "").toLowerCase(), d);
    for (const c of custom) seen.set((c.label || "").toLowerCase(), c);
    return Array.from(seen.values());
  }, [homeItems, parent]);
}

/**
 * Merge the admin Hero config with the built-in hero defaults so the page
 * always renders. Admin fields, when present (non-empty), win.
 */
export function useHero() {
  const { hero } = useCatalog();
  return useMemo(() => {
    const pick = (a, b) => (a && String(a).trim() ? a : b);
    const pickNum = (a, b) => (Number.isFinite(Number(a)) && Number(a) > 0 ? Number(a) : b);
    const pickBool = (a, b) => (typeof a === "boolean" ? a : b);
    return {
      eyebrow:            pick(hero?.eyebrow, "The Alankar House"),
      titleLine1:         pick(hero?.titleLine1, "Every ornament"),
      titleLine2:         pick(hero?.titleLine2, "tells a story."),
      lead:               pick(
        hero?.lead,
        "Trendsetting gold, diamond & heritage jewellery suited for every occasion — hallmarked, honestly priced, and hand-finished at our Rasta Peth workshop."
      ),
      ctaPrimaryLabel:    pick(hero?.ctaPrimaryLabel, "Shop Collection"),
      ctaPrimaryLink:     pick(hero?.ctaPrimaryLink, "/collections/gold"),
      ctaSecondaryLabel:  pick(hero?.ctaSecondaryLabel, "Explore Diamond"),
      ctaSecondaryLink:   pick(hero?.ctaSecondaryLink, "/collections/diamond"),
      mainImage:          pick(hero?.mainImage, HERO_MAIN),
      productImage:       pick(hero?.productImage, HERO_PRODUCT),
      productName:        pick(hero?.productName, HERO_PRODUCT_NAME),
      // brand + workshop
      // No default logo — center stays empty until admin uploads one.
      logoUrl:            pick(hero?.logoUrl, ""),
      craftsmanshipVideo: pick(
        hero?.craftsmanshipVideo,
        "https://customer-assets.emergentagent.com/job_gold-shine-pune/artifacts/ocouzlj9_gemini_generated_video_C9D1C25A.MP4"
      ),
      craftsmanshipPoster: pick(
        hero?.craftsmanshipPoster,
        "https://images.unsplash.com/photo-1583937443351-c8b73aa4b6ff?auto=format&fit=crop&w=1600&q=85"
      ),
      // Full-screen hero video (auto-plays behind the navbar). Default is a jewellery
      // workshop clip — admin can replace it via Homepage > Hero.
      heroVideo: pick(
        hero?.heroVideo,
        "https://customer-assets.emergentagent.com/job_gold-shine-pune/artifacts/ocouzlj9_gemini_generated_video_C9D1C25A.MP4"
      ),
      heroVideoPoster: pick(
        hero?.heroVideoPoster,
        "https://images.unsplash.com/photo-1611652022419-a9419f74343d?auto=format&fit=crop&w=2000&q=85"
      ),
      // Brand + navbar look
      logoWidth:      pickNum(hero?.logoWidth, 64),        // logo image width in px
      textLogoImage:  pick(hero?.textLogoImage, ""),       // optional PNG wordmark
      subNavColor:    pick(hero?.subNavColor, "#2B1810"),  // dark brown default
      subNavAnimation: pickBool(hero?.subNavAnimation, true),
      // Nav transparency sliders (0-100). Frontend converts to rgba alpha.
      navOpacity:     pickNum(hero?.navOpacity, 55),       // default 55% (matches existing frost)
      subNavOpacity:  pickNum(hero?.subNavOpacity, 32),    // default 32% (video shines through)
      // Optional centered promo banner shown on site load
      bannerImage:    pick(hero?.bannerImage, ""),
      bannerEnabled:  pickBool(hero?.bannerEnabled, false),
      bannerLink:     pick(hero?.bannerLink, ""),
    };
  }, [hero]);
}

// Perfect Match tile labels are kept static (Metal / Occasion / Style),
// but the destinations under each are the FPM options above.
export const FPM_TILES = PERFECT_MATCH;

/**
 * Merge admin Store Settings with sensible defaults so every component that
 * shows phone / address / social links stays in sync when the admin edits them.
 */
const STORE_DEFAULTS = {
  phone: "+91 95036 50463",
  whatsapp: "919503650463",
  email: "hasmukhsoni59@gmail.com",
  addressLine1: "499/B, Shiv Sadan, 2nd Shop,",
  addressLine2: "Rasta Peth, Pune — 411011",
  hours: "Mon – Sat · 11 am – 8 pm",
  instagram:
    "https://www.instagram.com/alankarjeweller?igsh=YjE5MzA2bnl6MnFu&utm_source=qr",
  facebook:
    "https://www.facebook.com/share/1JiPBsawbH/?mibextid=wwXIfr",
};

const normaliseWhatsApp = (raw) => {
  if (!raw) return STORE_DEFAULTS.whatsapp;
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return STORE_DEFAULTS.whatsapp;
  // If the admin entered a 10-digit Indian number, prepend country code 91.
  return digits.length === 10 ? `91${digits}` : digits;
};

export function useStoreSettings() {
  const { storeSettings } = useCatalog();
  return useMemo(() => {
    const pick = (a, b) => (a && String(a).trim() ? String(a).trim() : b);
    const s = storeSettings || {};
    const merged = {
      phone: pick(s.phone, STORE_DEFAULTS.phone),
      whatsapp: normaliseWhatsApp(s.whatsapp || STORE_DEFAULTS.whatsapp),
      email: pick(s.email, STORE_DEFAULTS.email),
      addressLine1: pick(s.addressLine1, STORE_DEFAULTS.addressLine1),
      addressLine2: pick(s.addressLine2, STORE_DEFAULTS.addressLine2),
      hours: pick(s.hours, STORE_DEFAULTS.hours),
      instagram: pick(s.instagram, STORE_DEFAULTS.instagram),
      facebook: pick(s.facebook, STORE_DEFAULTS.facebook),
    };
    const encodedAddress = encodeURIComponent(
      `Alankar Jewellers, ${merged.addressLine1} ${merged.addressLine2}`
    );
    merged.waBase = `https://wa.me/${merged.whatsapp}`;
    merged.waGeneric = `${merged.waBase}?text=${encodeURIComponent(
      "Hi Alankar Jewellers, I have an enquiry."
    )}`;
    merged.waLink = (name) =>
      `${merged.waBase}?text=${encodeURIComponent(
        `Hi Alankar Jewellers, I'd like to know more about: ${name}`
      )}`;
    merged.telHref = `tel:${merged.phone.replace(/\s+/g, "")}`;
    merged.mailHref = `mailto:${merged.email}`;
    merged.mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    merged.directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    merged.mapEmbedUrl = `https://maps.google.com/maps?q=${encodedAddress}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
    return merged;
  }, [storeSettings]);
}
